import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ContractSidebar from './components/ContractSidebar';
import ContractViewer from './components/ContractViewer';
import SignaturePanel from './components/SignaturePanel';
import { supabase } from '../../lib/supabase';


const ClientContractManagementPortal = () => {
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [showRecommendations, setShowRecommendations] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadContracts();
      loadNotifications();
      loadRecommendations();
      setupRealtimeSubscription();
    }
  }, [user]);

  const loadContracts = async () => {
    try {
      setLoading(true);
      
      // Get contracts where user is a signer
      const { data: signatureRequests, error: sigError } = await supabase?.from('signature_requests')?.select(`
          *,
          contract:contracts (
            *,
            property:properties (title, address, property_type),
            client:clients (name, email, phone),
            buyer_representation:buyer_representation_contracts(
              id,
              arddjo_retainer_percentage,
              client_retainer_percentage,
              arddjo_retainer_amount,
              client_retainer_amount,
              representation_duration_months,
              exclusive_representation,
              property_types,
              budget_min,
              budget_max,
              preferred_locations,
              arddjo_acceptance_status,
              client_acceptance_status
            )
          )
        `)?.eq('signer_id', user?.id)?.order('requested_at', { ascending: false });

      if (sigError) throw sigError;

      // Transform data
      const contractsData = signatureRequests?.map(req => ({
        id: req?.contract?.id,
        contractNumber: req?.contract?.contract_number,
        contractType: req?.contract?.contract_type,
        status: req?.status,
        signatureStatus: req?.status,
        property: req?.contract?.property,
        client: req?.contract?.client,
        startDate: req?.contract?.start_date,
        endDate: req?.contract?.end_date,
        amount: req?.contract?.amount,
        requestedAt: req?.requested_at,
        dueDate: req?.due_date,
        signatureRequestId: req?.id,
        documents: req?.contract?.documents || [],
        buyerRepresentation: req?.contract?.buyer_representation?.[0] || null
      })) || [];

      setContracts(contractsData);
      
      // Auto-select first contract
      if (contractsData?.length > 0 && !selectedContract) {
        setSelectedContract(contractsData?.[0]);
      }
    } catch (err) {
      setError(err?.message || 'فشل في تحميل العقود');
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const { data, error } = await supabase?.from('signature_reminders')?.select('*')?.eq('status', 'pending')?.order('scheduled_for', { ascending: true })?.limit(5);

      if (error) throw error;
      setNotifications(data || []);
    } catch (err) {
      console.error('Error loading notifications:', err);
    }
  };

  const loadRecommendations = async () => {
    try {
      // Get client ID for this user
      const { data: clientData, error: clientError } = await supabase
        ?.from('clients')
        ?.select('id')
        ?.eq('email', user?.email)
        ?.single();

      if (clientError || !clientData) {
        console.log('No client profile found for user');
        return;
      }

      // Get approved recommendations for this client that have been sent
      const { data, error } = await supabase
        ?.from('client_recommendations')
        ?.select('*')
        ?.eq('client_id', clientData?.id)
        ?.eq('approval_status', 'approved')
        ?.eq('sent_to_client', true)
        ?.order('sent_at', { ascending: false });

      if (error) throw error;
      
      setRecommendations(data?.map(row => ({
        id: row?.id,
        recommendationData: row?.recommendation_data,
        sentAt: row?.sent_at,
        createdAt: row?.created_at
      })) || []);
    } catch (err) {
      console.error('Error loading recommendations:', err);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = supabase?.channel('client-contracts')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'signature_requests',
          filter: `signer_id=eq.${user?.id}`
        },
        (payload) => {
          if (payload?.eventType === 'UPDATE') {
            loadContracts();
          }
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/client-portal-authentication-gateway');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleContractSelect = (contract) => {
    setSelectedContract(contract);
  };

  const handleSignatureComplete = () => {
    loadContracts();
    alert('تم التوقيع على العقد بنجاح');
  };

  const filteredContracts = contracts?.filter(contract => {
    // Category filter
    if (activeCategory !== 'all') {
      if (activeCategory === 'pending' && contract?.signatureStatus !== 'pending') return false;
      if (activeCategory === 'signed' && contract?.signatureStatus !== 'signed') return false;
      if (activeCategory === 'expired' && contract?.signatureStatus !== 'expired') return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      return (
        contract?.contractNumber?.toLowerCase()?.includes(query) ||
        contract?.contractType?.toLowerCase()?.includes(query) ||
        contract?.property?.title?.toLowerCase()?.includes(query)
      );
    }

    return true;
  });

  const stats = {
    total: contracts?.length || 0,
    pending: contracts?.filter(c => c?.signatureStatus === 'pending')?.length || 0,
    signed: contracts?.filter(c => c?.signatureStatus === 'signed')?.length || 0,
    expired: contracts?.filter(c => c?.signatureStatus === 'expired')?.length || 0
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">جاري تحميل العقود...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" dir="rtl">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-50">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo & Title */}
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600">
                <Icon name="FileText" size={24} color="#000" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">بوابة إدارة العقود</h1>
                <p className="text-sm text-gray-400">مرحباً، {userProfile?.full_name || 'عميل'}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4">
              {/* Notifications */}
              <button className="relative p-2 text-gray-400 hover:text-white transition-colors">
                <Icon name="Bell" size={20} />
                {notifications?.length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>

              {/* Logout */}
              <Button
                variant="outline"
                size="sm"
                iconName="LogOut"
                onClick={handleLogout}
              >
                تسجيل الخروج
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Recommendations Panel */}
      {showRecommendations && recommendations?.length > 0 && (
        <div className="bg-gradient-to-r from-primary/5 to-accent/5 border-b border-primary/20">
          <div className="container mx-auto px-4 md:px-6 py-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Icon name="Sparkles" size={24} color="var(--color-primary)" />
                <h2 className="text-lg font-heading font-bold text-foreground">
                  توصيات ذكية مخصصة لك
                </h2>
              </div>
              <button
                onClick={() => setShowRecommendations(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="إغلاق التوصيات"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recommendations?.map((rec) => {
                const data = rec?.recommendationData;
                const topMatch = data?.propertyMatches?.[0];
                return (
                  <div key={rec?.id} className="bg-card border border-border rounded-lg p-4 hover:shadow-warm-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                          <Icon name="Home" size={20} color="var(--color-primary)" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">
                            {data?.propertyMatches?.length} عقار مطابق
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(rec?.sentAt)?.toLocaleDateString('ar-EG')}
                          </p>
                        </div>
                      </div>
                      {topMatch && (
                        <div className="flex items-center gap-1 bg-success/10 px-2 py-1 rounded-full">
                          <Icon name="TrendingUp" size={14} color="var(--color-success)" />
                          <span className="text-xs font-bold text-success">{topMatch?.matchScore}%</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-foreground mb-3 line-clamp-2">{data?.summary}</p>
                    {topMatch && (
                      <div className="bg-muted/30 rounded-lg p-3 mb-3">
                        <p className="text-xs font-semibold text-muted-foreground mb-1">أفضل تطابق:</p>
                        <p className="text-sm text-foreground font-medium mb-2">{topMatch?.reasoning}</p>
                        {topMatch?.highlights && topMatch?.highlights?.length > 0 && (
                          <div className="space-y-1">
                            {topMatch?.highlights?.slice(0, 2)?.map((highlight, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <Icon name="CheckCircle" size={12} color="var(--color-success)" className="mt-0.5" />
                                <span className="text-xs text-foreground">{highlight}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {data?.contractTiming && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="Calendar" size={14} />
                        <span>التوقيت الأمثل: {data?.contractTiming?.optimalReviewDate}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex h-[calc(100vh-73px)]">
        {/* Left Sidebar - Contract List (25%) */}
        <ContractSidebar
          contracts={filteredContracts}
          selectedContract={selectedContract}
          onContractSelect={handleContractSelect}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          stats={stats}
        />

        {/* Center - Contract Viewer (60%) */}
        <ContractViewer
          contract={selectedContract}
          onRefresh={loadContracts}
        />

        {/* Right Panel - Signature Tools (15%) */}
        <SignaturePanel
          contract={selectedContract}
          onSignatureComplete={handleSignatureComplete}
        />
      </div>
    </div>
  );
};

export default ClientContractManagementPortal;